import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  ArrowLeft, Building2, Target, Layers, FileQuestion,
  Users, Settings, Mail, CheckCircle2, ChevronRight,
  Lightbulb, BookOpen, Bell, AlertTriangle, Lock
} from 'lucide-react';

const SETUP_SECTIONS = [
  {
    id: 'project-details',
    icon: Building2,
    title: '1. Project Details',
    description: 'Start by setting up your project basics.',
    steps: [
      'Navigate to your project\'s Admin Dashboard',
      'Click on Project Details to open the editor',
      'Set a clear project name that your stakeholders will recognize',
      'Add a comprehensive project description',
      'Fill in the Project Manager, Contact Email, and key dates (Baseline & Actual)',
    ],
    tip: 'A comprehensive project description will help the AI analysis understand people\'s responses in the context of the project.',
  },
  {
    id: 'drivers',
    icon: Target,
    title: '2. Drivers',
    description: 'Define the key themes or dimensions you want to measure.',
    steps: [
      'Go to Administration > Drivers',
      'Add drivers that represent the key themes of your assessment (e.g., Communication, Leadership, Trust)',
      'Give each driver a clear name and description so respondents understand the context',
      'Use the Quick Start library to import a professionally curated set of drivers',
      'Drag and reorder drivers to set the priority order they appear in dashboards',
      'Each driver will group related questions together in the analysis',
    ],
    tip: 'Most projects work best with 4-8 drivers. Too many can overwhelm respondents.',
  },
  {
    id: 'segments',
    icon: Layers,
    title: '3. Segments',
    description: 'Create segment types and segments to categorize stakeholders.',
    steps: [
      'Go to Administration > Segments',
      'First create Segment Types (e.g., "Organization", "Location", "Role")',
      'Then create Segments within each type (e.g., "Contoso", "Perth", "Manager")',
      'Segments enable filtering and comparison in the analysis dashboards',
      'Each stakeholder can belong to one segment per segment type',
    ],
    tip: 'Start with Organization as your first segment type — it\'s the most common grouping.',
  },
  {
    id: 'questions',
    icon: FileQuestion,
    title: '4. Questions',
    description: 'Build the survey with different question categories.',
    steps: [
      'Go to Administration > Questions',
      'Public questions are visible to everyone and gather general project sentiment',
      'About Me questions are self-reflective and confidential to each respondent',
      'About Others questions allow stakeholders to assess their peers',
      'Mapping Categories help categorize relationships between stakeholders',
      'Assign questions to drivers so they appear in the Driver Analysis dashboard',
    ],
    tip: 'Use the Quick Start import to get a professionally designed question set instantly.',
  },
  {
    id: 'stakeholders',
    icon: Users,
    title: '5. Stakeholders',
    description: 'Add and manage the people who will participate.',
    steps: [
      'Go to Administration > Stakeholders',
      'Import stakeholders via CSV or add them manually',
      'Assign each stakeholder to the appropriate segments',
      'Stakeholder statuses: Pending (not yet invited), Invited (email sent), Reference (completed), Rejected, Anonymous',
      'Send invitations via the bulk invite feature when ready',
    ],
    tip: 'Ensure email addresses are correct before sending invitations — it saves time on bounce handling.',
  },
  {
    id: 'dashboard-config',
    icon: Settings,
    title: '6. Dashboard Configuration',
    description: 'Set up the thresholds and preferences for analysis views.',
    steps: [
      'On the Admin Dashboard, click the Dashboard Configuration card to view current settings',
      'This links to the detailed configuration page where dashboard preferences are managed',
      'Settings configured here affect what all stakeholders see in their dashboards',
    ],
    tip: 'Review Dashboard Configuration early — it determines how your data is presented across all views.',
  },
  {
    id: 'notification-settings',
    icon: Bell,
    title: '7. Notification Settings',
    description: 'Choose how and when you receive alerts about stakeholder activity.',
    steps: [
      'On the Admin Dashboard, scroll to the Notification Settings card',
      'Select Immediate to get an email each time a stakeholder submits feedback',
      'Select Daily Summary to receive one consolidated email per day',
      'Select None to disable submission notifications entirely',
      'Save your preference — you can change it at any time',
    ],
    tip: 'Daily Summary is a great balance — you stay informed without inbox overload.',
  },
  {
    id: 'anonymity-settings',
    icon: AlertTriangle,
    title: '8. Anonymity Settings',
    description: 'Protect stakeholder privacy by setting minimum response thresholds.',
    steps: [
      'On the Admin Dashboard, scroll to the Anonymity Settings card',
      'Set the minimum number of responses required before a segment\'s data is shown',
      'Default threshold is 3 — segments with fewer responses display "insufficient data"',
      'Choose a higher threshold (5 or 10) for projects where privacy is especially sensitive',
    ],
    tip: 'A threshold of 3-5 balances data richness with respondent anonymity.',
  },
  {
    id: 'access-gates',
    icon: Lock,
    title: '9. Dashboard Access Requirements',
    description: 'Control when users can access dashboards based on completion progress.',
    steps: [
      'On the Admin Dashboard, scroll to the Dashboard Access Requirements card',
      'Enable "Require About Me Completion" to ensure users fill out their profile before accessing dashboards',
      'Set a minimum number of About Others mappings required before People Galaxy becomes visible',
      'Use the "Preview what users will see" links to see exactly what blocked users encounter',
      'Admins always have full access regardless of these settings',
    ],
    tip: 'Requiring About Me completion first encourages users to engage with the platform thoughtfully before viewing results.',
  },
  {
    id: 'email-settings',
    icon: Mail,
    title: '10. Email Settings',
    description: 'Configure automated emails and reminders.',
    steps: [
      'On the Admin Dashboard, click the Email Configuration card to go to settings',
      'Set the "From Name" that appears in invitation and reminder emails',
      'Configure reminder schedules for stakeholders who haven\'t completed their surveys',
      'Enable or disable automated daily summary emails for admins',
    ],
    tip: 'A personal "From Name" (e.g., "Sarah from Project Alpha") increases response rates significantly.',
  },
];

export default function AdminHelpPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-6" data-testid="admin-help-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/project/${projectId}/admin-dashboard`)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-[#5AA2AE]" />
          Admin Setup Guide
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Follow these steps to configure your Pulse project from start to finish.
        </p>
      </div>

      {/* Step-by-step guide */}
      <Accordion type="multiple" defaultValue={['project-details']} className="space-y-3">
        {SETUP_SECTIONS.map((section) => (
          <AccordionItem key={section.id} value={section.id} className="border rounded-lg bg-white overflow-hidden">
            <AccordionTrigger className="px-5 py-4 hover:bg-slate-50 [&[data-state=open]]:bg-slate-50">
              <div className="flex items-center gap-3 text-left">
                <section.icon className="w-5 h-5 text-[#5AA2AE] flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">{section.title}</p>
                  <p className="text-xs text-slate-400 font-normal">{section.description}</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4">
              <div className="space-y-4 pt-2">
                <ol className="space-y-2.5 ml-1">
                  {section.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-500 
                                       flex items-center justify-center text-xs font-medium mt-0.5">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>

                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">{section.tip}</p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Footer */}
      <Card className="bg-[#5AA2AE]/5 border-[#5AA2AE]/20">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Need more help?</p>
              <p className="text-xs text-slate-500">Visit the Help page for FAQs and support contact.</p>
            </div>
            <Button variant="outline" size="sm"
              onClick={() => navigate(`/project/${projectId}/help`)}
              data-testid="go-to-help-btn"
            >
              Go to Help <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
